// NewsProxyAPIManager.js
// Neutral manager that calls your internal proxy (can aggregate any provider).
class NewsProxyAPIManager {
    constructor() {
        this.isInitialized = false;
        this.baseUrl = 'https://api.palosverdestech.com/news/';
    }

    async initialize() {
        // No API keys or config needed for the proxy
        this.isInitialized = true;
    }

    static async create() {
        if (!NewsProxyAPIManager.instance) {
            const instance = new NewsProxyAPIManager();
            await instance.initialize();
            NewsProxyAPIManager.instance = instance;
        }
        return NewsProxyAPIManager.instance;
    }

    /**
     * Fetch news for a symbol. Prefer "EXCHANGE:TICKER" (e.g., "NYSE:IBM").
     * Extra params kept for compatibility with existing callers but unused.
     */
    async fetchStockNews(stockSymbol, _limit = 10, _startDate = null, _endDate = null) {
        try {
            if (!this.isInitialized) throw new Error('Proxy client not initialized.');

            // Proxy expects a plain path with a literal colon: /news/NYSE:IBM
            const url = this.baseUrl + stockSymbol;
            if (__LOG_INFO__) AppLog.info(`📡 Proxy fetch: ${url}`);

            const response = await fetch(url, { method: 'GET' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const json = await response.json();

            // Accept several shapes: [], {articles:[]}, {results:[]}, {data:[]}
            const rows = Array.isArray(json) ? json
                : Array.isArray(json.articles) ? json.articles
                    : Array.isArray(json.results) ? json.results
                        : Array.isArray(json.data) ? json.data
                            : [];

            // Normalize fields expected by UI with HTML entity decoding
            const articles = rows.map(a => ({
                title: he.decode(a.title || a.headline || 'No title available'),
                publisher:
                    (a.source && a.source.name) ? a.source.name :
                        (a.source === 'lulegacy' ?
                            (a.url ? a.url.split('/')[2].split('.').slice(-2).join('.') : 'Unknown publisher') :
                            (a.source || a.publisher || (a.domain ? a.domain : 'Unknown publisher'))),
                publishedDate: a.published_at || a.publishedAt || a.date || a.published || new Date().toISOString(),
                url: a.url || a.article_url || '#',
                description: he.decode(a.description || a.summary || ''),
                tickers: Array.isArray(a.tickers) && a.tickers.length ? a.tickers : [stockSymbol]
            }));

            const limitedArticles = articles.slice(0, 7);

            if (__LOG_INFO__) AppLog.info(`✅ Proxy returned ${articles.length} articles, displaying ${limitedArticles.length} for ${stockSymbol}`);
            return limitedArticles;
        } catch (err) {
            if (__LOG_ERROR__) AppLog.error(`❌ Proxy news fetch failed for ${stockSymbol}: ${err.message}`);
            return [];
        }
    }

    async getWatchlistNews(userId, watchlistId) {
        try {
            if (!this.isInitialized) throw new Error('Proxy client not initialized.');

            // URL: /news/watchlist/:userID/:watchlistId
            const url = `${this.baseUrl}watchlist/${userId}/${watchlistId}`;
            if (__LOG_INFO__) AppLog.info(`📡 Proxy fetch watchlist news: ${url}`);

            const response = await fetch(url, { method: 'GET' });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const json = await response.json();
            
             // Accept several shapes: [], {articles:[]}, {results:[]}, {data:[]}
            const rows = Array.isArray(json) ? json
                : Array.isArray(json.articles) ? json.articles
                    : Array.isArray(json.results) ? json.results
                        : Array.isArray(json.data) ? json.data
                            : [];

            // Normalize fields expected by UI with HTML entity decoding
            const articles = rows.map(a => ({
                title: he.decode(a.title || a.headline || 'No title available'),
                publisher:
                    (a.source && a.source.name) ? a.source.name :
                        (a.source === 'lulegacy' ?
                            (a.url ? a.url.split('/')[2].split('.').slice(-2).join('.') : 'Unknown publisher') :
                            (a.source || a.publisher || (a.domain ? a.domain : 'Unknown publisher'))),
                publishedDate: a.published_at || a.publishedAt || a.date || a.published || new Date().toISOString(),
                url: a.url || a.article_url || '#',
                description: he.decode(a.description || a.summary || ''),
                tickers: Array.isArray(a.tickers) ? a.tickers : []
            }));

            const limitedArticles = articles.slice(0, 7);

            if (__LOG_INFO__) AppLog.info(`✅ Proxy returned ${articles.length} articles, displaying ${limitedArticles.length} for watchlist ${watchlistId}`);
            return limitedArticles;

        } catch (err) {
            if (__LOG_ERROR__) AppLog.error(`❌ Proxy watchlist news fetch failed for ${watchlistId}: ${err.message}`);
            return [];
        }
    }
}

// Expose to window like your Mediastack manager does
window.NewsProxyAPIManager = NewsProxyAPIManager;
